import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { FEATURES } from '../lib/features';
import PropertyFeaturesModal from '../components/properties/PropertyFeaturesModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Building2, Plus, Trash2, LogIn, Users, Newspaper, Loader2, Star, ShieldCheck, Pencil, Check, X, Download, SlidersHorizontal,
} from 'lucide-react';

const PropertiesPage = () => {
  const { user, switchProperty, checkAuth } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // property being renamed
  const [editName, setEditName] = useState('');
  const [backingUp, setBackingUp] = useState(false);
  const [featuresFor, setFeaturesFor] = useState(null); // property whose features are being edited
  const activePropId = user?.active_property_id;

  const load = async () => {
    setLoading(true);
    try {
      setProperties(await api.get('/properties'));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === 'super_admin') load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Whole-system backup: one ZIP with every property's backup + the property
  // list + all users. Per-property backups cannot recover a deleted property.
  const handleSystemBackup = async () => {
    setBackingUp(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup-all`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Backup failed (${response.status})`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      a.download = match ? match[1] : `publishos_SYSTEM_backup_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('System backup failed:', err);
      alert('System backup failed. Please try again.');
    } finally {
      setBackingUp(false);
    }
  };

  const handleEnter = async (p) => {
    if (p.id === activePropId) return;
    await switchProperty(p.id);
  };

  const handleDelete = async (p) => {
    const typed = window.prompt(
      `This permanently deletes "${p.name}", ALL of its data (advertisers, bookings, invoices, editions, settings) and its ${p.user_count} user(s). This cannot be undone.\n\nType the property name to confirm:`
    );
    if (typed == null) return;
    if (typed.trim() !== p.name) {
      alert('Name did not match. Deletion cancelled.');
      return;
    }
    try {
      const res = await api.delete(`/properties/${p.id}`);
      alert(`Deleted "${res.deleted_property}" and ${res.deleted_users} user(s).`);
      load();
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const saveRename = async (p) => {
    if (!editName.trim() || editName.trim() === p.name) { setEditing(null); return; }
    try {
      await api.put(`/properties/${p.id}`, { name: editName.trim() });
      setEditing(null);
      load();
    } catch (e) {
      alert('Rename failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-6" data-testid="properties-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#1E293B] flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#3B82F6]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight">Properties</h1>
            <p className="text-[#94A3B8] mt-1">Each publication runs on its own isolated data, settings & backups.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleSystemBackup}
            disabled={backingUp}
            title="One ZIP with every property's backup, the property list and all users"
            className="border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B] hover:text-[#F8FAFC]"
            data-testid="system-backup-btn"
          >
            {backingUp
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            {backingUp ? 'Creating backup...' : 'Back Up All Properties'}
          </Button>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            data-testid="create-property-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Property
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="properties-grid">
          {properties.map((p) => {
            const isActive = p.id === activePropId;
            return (
              <div
                key={p.id}
                className={`bg-[#121212] border rounded-lg p-5 flex flex-col gap-4 transition-colors ${isActive ? 'border-[#3B82F6]' : 'border-[#1E293B]'}`}
                data-testid={`property-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-md bg-[#3B82F6]/10 flex items-center justify-center shrink-0">
                      <Newspaper className="w-5 h-5 text-[#3B82F6]" />
                    </div>
                    <div className="min-w-0">
                      {editing === p.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="h-8 bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] text-sm"
                            data-testid={`property-rename-input-${p.id}`}
                            autoFocus
                          />
                          <button onClick={() => saveRename(p)} className="text-emerald-400 p-1" data-testid={`property-rename-save-${p.id}`}><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditing(null)} className="text-[#94A3B8] p-1"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <h3 className="font-semibold text-[#F8FAFC] truncate flex items-center gap-2" data-testid={`property-name-${p.id}`}>
                          {p.name}
                          {p.is_primary && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" title="Primary property" />}
                        </h3>
                      )}
                      <p className="text-xs text-[#475569] font-mono truncate">{p.slug}</p>
                    </div>
                  </div>
                  {isActive && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#3B82F6]/15 text-[#3B82F6] font-medium shrink-0" data-testid={`property-active-badge-${p.id}`}>
                      Active
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-[#94A3B8]">
                    <Users className="w-4 h-4 text-[#475569]" />
                    <span><span className="text-[#F8FAFC] font-medium">{p.user_count}</span> users</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#94A3B8]">
                    <Building2 className="w-4 h-4 text-[#475569]" />
                    <span><span className="text-[#F8FAFC] font-medium">{Number(p.advertiser_count || 0).toLocaleString()}</span> clients</span>
                  </div>
                </div>

                <p className="text-xs text-[#475569]" data-testid={`property-features-summary-${p.id}`}>
                  {(() => {
                    const off = FEATURES.filter(f => p.features && p.features[f.key] === false);
                    if (off.length === 0) return 'All modules on';
                    if (off.length === FEATURES.length) return 'Sales team only (publishing modules off)';
                    return `Off: ${off.map(f => f.label).join(', ')}`;
                  })()}
                </p>

                <div className="flex items-center gap-2 pt-1 mt-auto border-t border-[#1E293B]/60">
                  <Button
                    size="sm"
                    onClick={() => handleEnter(p)}
                    disabled={isActive}
                    className="bg-[#3B82F6] hover:bg-[#2563EB] text-white flex-1 mt-3"
                    data-testid={`enter-property-btn-${p.id}`}
                  >
                    <LogIn className="w-4 h-4 mr-1.5" /> {isActive ? 'Current' : 'Enter'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setEditing(p.id); setEditName(p.name); }}
                    className="border-[#1E293B] bg-[#0A0A0A] text-[#94A3B8] hover:text-[#F8FAFC] mt-3"
                    data-testid={`rename-property-btn-${p.id}`}
                    title="Rename"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setFeaturesFor(p)}
                    className="border-[#1E293B] bg-[#0A0A0A] text-[#94A3B8] hover:text-[#F8FAFC] mt-3"
                    data-testid={`features-property-btn-${p.id}`}
                    title="Features"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                  </Button>
                  {!p.is_primary && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(p)}
                      className="border-red-900/50 bg-[#0A0A0A] text-red-400 hover:bg-red-900/20 mt-3"
                      data-testid={`delete-property-btn-${p.id}`}
                      title="Delete property"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreatePropertyModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => { setShowCreate(false); load(); }}
      />
      <PropertyFeaturesModal
        property={featuresFor}
        onClose={() => setFeaturesFor(null)}
        onSaved={async () => {
          const changedActive = featuresFor && featuresFor.id === activePropId;
          setFeaturesFor(null);
          await load();
          // Menus are built from the logged-in user's property: refresh it
          // when the property being used right now was the one changed.
          if (changedActive && checkAuth) await checkAuth();
        }}
      />
    </div>
  );
};

const CreatePropertyModal = ({ isOpen, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', admin_name: '', admin_email: '', admin_password: '', sales_only: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) { setForm({ name: '', admin_name: '', admin_email: '', admin_password: '', sales_only: false }); setError(''); }
  }, [isOpen]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/properties', form);
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to create property');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-md" data-testid="create-property-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#3B82F6]" /> New Property</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Property / Publication Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Baja News"
              required
              className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
              data-testid="new-property-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Type</Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: false, t: 'Publication', d: 'Everything on: bookings, editions, accounting…' },
                { v: true, t: 'Sales team only', d: 'Clients and Sales. Publishing modules off.' },
              ].map(o => (
                <button
                  type="button" key={o.t}
                  onClick={() => setForm({ ...form, sales_only: o.v })}
                  className={`text-left rounded-md border p-3 ${form.sales_only === o.v ? 'border-[#3B82F6] bg-[#3B82F6]/10' : 'border-[#1E293B] bg-[#0A0A0A]'}`}
                  data-testid={`new-property-type-${o.v ? 'sales' : 'publication'}`}
                >
                  <div className="text-sm text-[#F8FAFC]">{o.t}</div>
                  <div className="text-xs text-[#475569]">{o.d}</div>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#475569]">You can change this later with the Features button on the property.</p>
          </div>

          <div className="p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md space-y-3">
            <p className="text-[11px] uppercase tracking-wide text-[#94A3B8] flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> First Admin Login
            </p>
            <div className="space-y-2">
              <Label className="text-[#94A3B8] text-sm">Admin Name *</Label>
              <Input
                value={form.admin_name}
                onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                placeholder="Full name"
                required
                className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9"
                data-testid="new-property-admin-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8] text-sm">Admin Email *</Label>
              <Input
                type="email"
                value={form.admin_email}
                onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                placeholder="admin@publication.com"
                required
                className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9"
                data-testid="new-property-admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8] text-sm">Admin Password *</Label>
              <Input
                type="text"
                value={form.admin_password}
                onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                placeholder="Set a strong password"
                required
                minLength={6}
                className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] h-9 font-mono"
                data-testid="new-property-admin-password-input"
              />
            </div>
            <p className="text-[11px] text-[#475569]">This admin can log in and manage this property. Data starts empty with default product types & industries.</p>
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2" data-testid="create-property-error">{error}</div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="submit-create-property-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : 'Create Property'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PropertiesPage;
